import { computePosition, flip, shift } from '@floating-ui/dom';
import { Editor, posToDOMRect, ReactRenderer } from '@tiptap/react';
import { MentionSelector } from './MentionSelector';
import { SuggestionOptions } from '@tiptap/suggestion';

const updatePosition = (editor: Editor, element: HTMLElement) => {
  const virtualElement = {
    getBoundingClientRect: () => posToDOMRect(editor.view, editor.state.selection.from, editor.state.selection.to),
  };

  computePosition(virtualElement, element, {
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [shift(), flip()],
  }).then(({ x, y, strategy }) => {
    element.style.width = 'max-content';
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
  });
};

export interface MentionItem {
  label: string;
  id: string;
}

export function getMentionsSetup(items: MentionItem[]) {
  return {
    items: ({ query }: { query: string }) => {
      return items
        .filter((item) => item.label.toLowerCase().includes(query.toLowerCase()))
        .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()))
        .slice(0, 5);
    },

    render: () => {
      let component: any;

      return {
        onStart: (props: any) => {
          component = new ReactRenderer(MentionSelector, {
            editor: props.editor,
            props,
          });

          if (!props.clientRect) {
            return;
          }

          component.element.style.position = 'absolute';

          document.body.appendChild(component.element);

          updatePosition(props.editor, component.element);
        },

        onUpdate(props: any) {
          component.updateProps(props);

          if (!props.clientRect) {
            return;
          }

          updatePosition(props.editor, component.element);
        },

        onKeyDown(props: any) {
          if (props.event.key === 'Escape') {
            component.destroy();

            return true;
          }

          return component.ref?.onKeyDown(props);
        },

        onExit() {
          component.element.remove();
          component.destroy();
        },
      };
    },

    command: ({ editor, range, props }) => {
      editor
        .chain()
        .focus()
        // first delete the typed query, except the last character
        .deleteRange({ from: range.from, to: range.to - 1 })
        .insertContent([
          { type: 'mention', attrs: props },
          // insert a zero width space to enforce caret placement
          { type: 'text', text: '\u200B\u200B' },
        ])
        // move the cursor to the end of the mention
        .setTextSelection(range.from + 2)
        // delete the last character, which is dangling
        .deleteRange({ from: range.from, to: range.from + 1 })
        .run();
    },
  } as Omit<SuggestionOptions, 'editor'>;
}
